import { useEffect, useState } from "react";
import { Column, Row, Spacer } from "../../components/Layout/Layout";
import { CommonContactResponse } from "../../core/api/contacts/types";
import { useQueryState } from "../../core/hooks/filters/useQueryState";
import useTranslations from "../../core/i18n/useTranslations";
import usePageState from "../../core/pagestate/usePageState";
import Button from "../../ui-lib/components/Button/Button";
import Divider from "../../ui-lib/components/Divider/Divider";
import SearchInput from "../../ui-lib/components/Inputs/SearchInput";
import PageHeader from "../../ui-lib/components/PageHeader/PageHeader";
import CreateCommonContactModal from "../Objects/ObjectResponse/Contacts/CreateCommonContactModal";
import DeleteContactModal from "../Objects/ObjectResponse/Contacts/DeleteContactModal";
import { CommonContactsTable } from "./CommonContactsTable";
import useUser from "../../core/user/useUser";
import {
  useStoredTableState,
  storeTableState,
} from "../../core/hooks/filters/useStoredTableState";
import {
  searchStringParser,
  urlSearchParser,
} from "../../ui-lib/utils/urlSearchParser";
import { ClearFilter } from "../../ui-lib/components/Tables/CleatFilters";
import { useCustomersFilter } from "../../core/hooks/filters/useCustomersFilter";
import { usePageFilter } from "../../core/hooks/filters/usePageFilter";
import { getCommonContacts } from "../../core/api/contacts/contacts";
import { notifyApiErrors } from "../../core/helpers/helpers";
import { CustomDropdown } from "../../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";

import styles from "../PagesLayout.module.css";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";

export const PAGE_ID = "adminportal/common-contacts";
const TYPE_OPTIONS = [
  { id: "true", label: "Contacts:input_resource" },
  { id: "false", label: "Common:contact_person" },
];

const CommonContacts = () => {
  const t = useTranslations();
  const pageState = usePageState();
  const user = useUser();

  storeTableState(PAGE_ID);
  const tableState = useStoredTableState(PAGE_ID);
  const searchState = urlSearchParser(tableState);

  const { customersFilter, setCustomersFilter, resetCustomersFilter } =
    useCustomersFilter(searchState);
  const { pageFilter, setPageFilter, resetPageFilter } =
    usePageFilter(searchState);
  const [tableSettings, setTableSettings] = useQueryState<{
    search: string;
    isResource?: string;
  }>({
    search: searchStringParser(searchState?.search) ?? "",
    isResource: searchState?.isResource,
  });

  const [total, setTotal] = useState<number>(0);
  const [tableData, setTableData] = useState<CommonContactResponse[]>([]);
  const [upForDelete, setUpForDelete] = useState<CommonContactResponse>();
  const [createCommonContactModalOpen, setCreateCommonContactModalOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCommonContacts = async (controller?: AbortController) => {
    setIsLoading(true);
    try {
      const result = await getCommonContacts(
        {
          page: pageFilter.page,
          pageSize: pageFilter.pageSize,
          search: tableSettings.search,
          organizationIds: customersFilter.customers,
          isResource: tableSettings.isResource,
        },
        user.authenticatedRequest,
        controller?.signal,
      );
      setTableData(result.data.contacts);
      setTotal(result.data.total);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      if (!controller?.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const onClear = () => {
    resetCustomersFilter();
    resetPageFilter();
    setTableSettings({ search: "", isResource: undefined });
  };

  const isFiltersApplied =
    !!customersFilter.customers ||
    !!tableSettings.search ||
    tableSettings.isResource !== undefined;

  useEffect(() => {
    const controller = new AbortController();
    fetchCommonContacts(controller);

    return () => controller.abort();
  }, [
    pageFilter.page,
    pageFilter.pageSize,
    customersFilter.customers,
    tableSettings.search,
    tableSettings.isResource,
  ]);

  const dropdownItems = TYPE_OPTIONS.map((opt) => ({
    ...opt,
    name: t(opt.label),
    isSelected: tableSettings.isResource === opt.id,
  }));

  return (
    <>
      <PageHeader
        pageTitle
        title={t("Menu:commoncontacts")}
        icon={pageState.pageIcon}
      >
        <Button
          variant="primary"
          text={t("Contacts:add_common_contact")}
          onClick={() => setCreateCommonContactModalOpen(true)}
          dataCy="add-contact-button"
        />
      </PageHeader>
      <Column className={styles.pageFilters}>
        <Row className={styles.settingsRow}>
          <SearchInput
            limit={1}
            value={tableSettings.search}
            onChange={(value) => {
              setTableSettings((prev) => ({ ...prev, search: value }));
              resetPageFilter();
            }}
            placeholder={t("Contacts:common_contacts_search_placeholder")}
          />
        </Row>
        <Spacer size={16} />
        <Divider />
        <Spacer size={16} />

        <Row className={styles.settingsRow}>
          <CustomDropdown
            customerFilter={customersFilter}
            onSelect={(ids: string) => setCustomersFilter({ customers: ids })}
          />
          <Spacer size={8} />

          <Dropdown
            withClearItem
            dataCy="contacts_type_picker"
            placeholder={`--${t("Common:type")}--`}
            width={156}
            selectedItem={dropdownItems.find((i) => i.isSelected)}
            items={dropdownItems}
            onSelectItem={(i) => {
              setTableSettings((prev) => ({
                ...prev,
                isResource: i.id === -1 ? undefined : i.id.toString(),
              }));
              resetPageFilter();
            }}
          />

          <Spacer size={8} />

          <ClearFilter
            text={t("Common:labels_clear_all_filters")}
            onClearClick={onClear}
            filtersToWatch={[
              customersFilter,
              tableSettings.search,
              tableSettings.isResource,
            ]}
          />
        </Row>
        <Spacer size={16} />
      </Column>
      <Column type="top" align="start" className={styles.content}>
        <CommonContactsTable
          data={tableData}
          isLoading={!tableData || isLoading}
          pageSettings={pageFilter}
          onPageSettingsChange={setPageFilter}
          onStartDelete={(contact) => {
            setUpForDelete(contact);
          }}
          isFiltersApplied={isFiltersApplied}
          onClear={onClear}
          totalItems={total}
          fetchCommonContacts={fetchCommonContacts}
        />
      </Column>

      <CreateCommonContactModal
        createCommonContactModalOpen={createCommonContactModalOpen}
        setCreateCommonContactModalOpen={setCreateCommonContactModalOpen}
      />

      <DeleteContactModal
        isOpen={!!upForDelete}
        contact={upForDelete}
        onClose={() => setUpForDelete(undefined)}
        onDelete={() => {
          setUpForDelete(undefined);
          fetchCommonContacts();
        }}
      />
    </>
  );
};

export default CommonContacts;
